import React, { useEffect, useState } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { api } from "@/lib/api";
import { Globe, MapPin, Loader2 } from "lucide-react";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

export const ClientWorldMap = () => {
  const [data, setData] = useState(null);
  const [hover, setHover] = useState(null);
  const [tip, setTip] = useState({ x: 0, y: 0 });

  useEffect(() => {
    api.get("/admin/geo").then(({ data }) => setData(data)).catch(() => setData({ points: [], countries: [] }));
  }, []);

  const points = data?.points || [];
  const countries = data?.countries || [];

  return (
    <div className="glass rounded-md p-6 mb-6" data-testid="world-map-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Globe className="w-5 h-5 text-aurora-cyan" />
          <h3 className="font-display text-lg font-semibold uppercase tracking-tight">Carte mondiale des clients</h3>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          {data ? <>{data.located} localisés · {countries.length} pays</> : "Chargement…"}
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_240px] gap-5">
        <div className="relative rounded-sm overflow-hidden border border-white/10 bg-[#070b16]">
          {!data && (
            <div className="absolute inset-0 grid place-items-center text-zinc-500 font-mono text-xs z-10">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          )}
          <ComposableMap
            projectionConfig={{ scale: 150 }}
            width={900} height={440}
            style={{ width: "100%", height: "auto" }}
            data-testid="world-map-svg"
          >
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill="#11182b"
                    stroke="#1f2a44"
                    strokeWidth={0.5}
                    style={{
                      default: { outline: "none" },
                      hover: { fill: "#16203a", outline: "none" },
                      pressed: { outline: "none" },
                    }}
                  />
                ))
              }
            </Geographies>
            {points.map((p) => (
              <Marker
                key={p.user_id}
                coordinates={[p.lon, p.lat]}
                onMouseEnter={(e) => { setHover(p); setTip({ x: e.clientX, y: e.clientY }); }}
                onMouseMove={(e) => setTip({ x: e.clientX, y: e.clientY })}
                onMouseLeave={() => setHover(null)}
              >
                <circle r={9} fill="#00E5FF" opacity={0.18}>
                  <animate attributeName="r" values="6;13;6" dur="2.4s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.25;0;0.25" dur="2.4s" repeatCount="indefinite" />
                </circle>
                <circle r={3.5} fill="#00E5FF" stroke="#04141b" strokeWidth={1} style={{ cursor: "pointer" }} />
              </Marker>
            ))}
          </ComposableMap>

          {hover && (
            <div
              className="fixed z-50 pointer-events-none bg-black/90 border border-aurora-cyan/40 rounded-sm px-3 py-2 shadow-xl"
              style={{ left: tip.x + 14, top: tip.y + 14 }}
            >
              <div className="font-display text-sm uppercase tracking-tight text-white">{hover.name || hover.email}</div>
              <div className="font-mono text-[10px] text-aurora-cyan mt-0.5 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {hover.city ? `${hover.city}, ` : ""}{hover.country}
              </div>
              <div className="font-mono text-[9px] text-zinc-500 mt-0.5">IP {hover.ip} · {hover.credits} crédits</div>
            </div>
          )}
        </div>

        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Répartition par pays</div>
          <div className="space-y-1.5 max-h-[400px] overflow-y-auto pr-1" data-testid="country-list">
            {countries.length === 0 && data && (
              <p className="font-mono text-[11px] text-zinc-600">Aucune donnée de localisation pour l'instant.</p>
            )}
            {countries.map((c) => {
              const max = countries[0]?.count || 1;
              return (
                <div key={c.country} className="flex items-center gap-2" data-testid={`country-row`}>
                  <div className="w-24 truncate font-mono text-[11px] text-zinc-300">{c.country}</div>
                  <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-aurora-cyan to-aurora-purple" style={{ width: `${(c.count / max) * 100}%` }} />
                  </div>
                  <div className="w-6 text-right font-mono text-[11px] text-white">{c.count}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
